import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import useAccessScope from "../../hooks/useAccessScope";
import useInterruptibleMutation from "../../hooks/useInterruptibleMutation";
import { Button, InlineAlert } from "../../components/BaseComponents";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type TierButtonProps = {
    value: string;
    current: string;
    onSubmit: (tier: string) => void
    loading?: boolean;
    disabled?: boolean;
}

function TierButton({value, current, onSubmit, loading, disabled}: TierButtonProps) {
    if(loading) {
        return (
            <Button type="Secondary" size="sm" disabled additionalClass="p-2" icon='fa-solid fa-spinner fa-spin text-white' />
        )
    }

    const selected = value === current;
    if(selected) {
        return (
            <Button type="Secondary" size="sm" disabled additionalClass="p-2" text="Current tier" />
        )
    } else {
        return (
            <Button type="Primary" size="sm" additionalClass="p-2" text="Set as tier" disabled={disabled} onClick={() => onSubmit(value)} />
        );
    }
}

type AdminCompanyTierProps = {
    companyId: string;
}

function AdminCompanyTier({companyId}: AdminCompanyTierProps) {
    const api = useApi();
    const { generation, isCurrent } = useAccessScope(api, companyId);
    const [loadedGeneration, setLoadedGeneration] = useState<number>();
    const startMutation = useInterruptibleMutation(api, companyId,
        'The tier change was interrupted and may already have been saved. Reload the organisation before trying again.');

    const [tier, setTier] = useState('');
    const [loading, setLoading] = useState(true);
    // Issue #884: the mount effect/onSubmit previously had no catch at all, so a rejecting GET
    // or write (including the now-rejecting READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE
    // notify-only codes - #857 item 6) left `loading` stuck true (every tier button spinning
    // forever) with no visible feedback, and no clean way to distinguish failure from success.
    const [loadError, setLoadError] = useState('');
    const [submitError, setSubmitError] = useState('');
    useEffect(() => {
        const controller = new AbortController();
        setTier('');
        setLoadedGeneration(generation);
        setLoading(true);
        setLoadError('');
        setSubmitError('');
        const run = async () => {
            try {
                const result = await api.get(`admin/company/${companyId}/tier`, { signal: controller.signal });
                if(controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setTier(result.tier);
            } catch (err) {
                if(controller.signal.aborted || !isCurrent(generation)) {
                    return;
                }
                setLoadError(getApiErrorMessage(err, 'Failed to load this organisation\'s tier. Please try again.'));
            } finally {
                if(!controller.signal.aborted && isCurrent(generation)) {
                    setLoading(false);
                }
            }
        }

        run();
        return () => controller.abort()
    }, [api, companyId, generation, isCurrent])

    const onSubmit = async (tier: string) => {
        if (loading || loadedGeneration !== generation || loadError) return;
        const { signal, onSubmitted, finish } = startMutation();
        setLoading(true);
        setSubmitError('');
        try {
            const csrf = await getCSRFToken(signal);
            if (signal.aborted || !isCurrent(generation)) return;
            onSubmitted();
            const result = await api.put(`admin/company/${companyId}/tier`, {
                tier,
                _csrf: csrf,
            }, { signal })
            if (signal.aborted || !isCurrent(generation)) return;
            setTier(result.tier);
        } catch (err) {
            if (!signal.aborted && isCurrent(generation)) {
                setSubmitError(getApiErrorMessage(err, 'Failed to update the tier. Please try again.'));
            }
        } finally {
            finish();
            if (!signal.aborted && isCurrent(generation)) setLoading(false);
        }
    }

    const scopeLoading = loading || loadedGeneration !== generation;
    const unavailable = !!loadError || !tier;
    if(tier === 'admin' && !scopeLoading) {
        return (
            <div>
                <h3 className="text-white mb-3 mt-8">Tier</h3>
                <div className="rounded bg-charcoal-600 p-5 space-y-5 text-white">
                    Organisation is Admin
                </div>
            </div>
        )
    }
    return (
        <div>
            <h3 className="text-white mb-3 mt-8">Tier</h3>
            { loadedGeneration === generation && loadError && <InlineAlert type='error' className='mb-4'>{ loadError }</InlineAlert> }
            { loadedGeneration === generation && submitError && <InlineAlert type='error' className='mb-4'>{ submitError }</InlineAlert> }
            <div className="rounded bg-charcoal-600 p-5 space-y-5">
                <div className="p-2 rounded bg-charcoal-400">
                    <div className="flex space-x-2">
                        <div className="w-40 text-white">Micro</div>
                        <TierButton value="micro" current={tier} onSubmit={onSubmit} loading={scopeLoading} disabled={unavailable} />
                    </div>
                    <ul>
                        <li className="text-success-500">✓ Create reports</li>
                        <li className="text-error-500">✗ Manage Accounting Periods</li>
                        <li className="text-error-500">✗ Insights</li>
                    </ul>
                </div>
                <div className="p-2 rounded bg-charcoal-400">
                    <div className="flex space-x-2">
                        <div className="w-40 text-white">Standard</div>
                        <TierButton value="standard" current={tier} onSubmit={onSubmit} loading={scopeLoading} disabled={unavailable} />
                    </div>
                    <ul>
                        <li className="text-success-500">✓ Create reports</li>
                        <li className="text-success-500">✓ Manage Accounting Periods</li>
                        <li className="text-error-500">✗ Insights</li>
                    </ul>
                </div>
                <div className="p-2 rounded bg-charcoal-400">
                    <div className="flex space-x-2">
                        <div className="w-40 text-white">Premium</div>
                        <TierButton value="premium" current={tier} onSubmit={onSubmit} loading={scopeLoading} disabled={unavailable} />
                    </div>
                    <ul>
                        <li className="text-success-500">✓ Create reports</li>
                        <li className="text-success-500">✓ Manage Accounting Periods</li>
                        <li className="text-success-500">✓ Insights</li>
                    </ul>
                </div>
            </div>
        </div>
    )
}

export default AdminCompanyTier;
